import React from "react";
import PropTypes from "prop-types";
import { getCurrentLocale } from "../../../localization";
import {
    MINIMUM_KMS_FOR_SCORE,
    NOT_APPLICABLE_SCORE,
    SUPPORTED_TIME_PERIOD_TYPES,
    REPORT_BY_TYPES,
    SUPPORTED_EVENTS_FACTORS_TYPES } from "./SafetyScore";
import SafetyScoreHeader from "./SafetyScoreHeader";
import ScoreSummary from "./Modules/ScoreSummary";
import ScoreTrend from "./Modules/ScoreTrend";
import EventsTrend from "./Modules/EventsTrend";
import ScoreDetailsTable from "./Modules/ScoreDetailsTable";
import SafetyScoreLandingPage from "./SafetyScoreLandingPage";

const PERFECT_SCORE = 100;
const LOWEST_POSSIBLE_SCORE = 0;
const EVENT_SCALING_FACTOR = 1000; // as per the scoring formula provided, we need to multiply each event by 1000 to normalize it to the total driving distance
export const MILES_TO_KM_FACTOR = 1.60934; // each event should be normalized to miles, but the source data is in kilometers

export const MAX_SCORE_TREND_PREVIOUS_PERIODS = 5; // ∴ 6 total, current period plus 5 previous periods
const MAX_CONCURRENT_API_REQUESTS = 4; // ∴ 4 requests at a time while processing reuqest queue
const MAX_DAYS_FOR_TRIP_QUERY_PAGINATION = 3; // ∴ 3 days per trip query
const MAX_DAYS_FOR_EVENTS_QUERY_PAGINATION = 2; // ∴ 2 days per events query
const SLOW_QUERY_RETRY_TIMEOUT = 1000; // ∴ 1 second polling interval for API requests

const REQUEST_TYPE = {
    TRIP: "TRIP",
    EVENTS: "EVENTS"
};

export const timePeriodShortLabelForDate = (date) => {
    if (!date) {
        console.warn("SafetyScoreModuleGroup timePeriodShortLabelForDate date is undefined");
        return;
    }
    return date.toLocaleString(getCurrentLocale(), { month: "short", day: "numeric" });
};

const INITIAL_STATE = {
    selectedTimePeriod: 0, // default time period is 0 (the current time period), and max value is MAX_SCORE_TREND_PREVIOUS_PERIODS
    // current period data, loads first
    score: 0,
    periodStartDate: undefined, // current period start Date
    periodEndDate: undefined,
    totalKMs: 0,
    totalSeconds: 0,
    totalEventsByType: { // totals of each event type, combined for all entities
        /* "EVENT_RAVEN_UNPLUGGED": 0,
        ...*/
    },
    scoreSummariesByEntityId: {/* // total events for each entity and summary metadata
        "<Entity ID>": {
            "driverName": "DimDriver.name",
            "vehicleName": "DimVehicle.name",
            "score": 0,
            "totalKMsForParentSegments": 0,
            "totalSeconds": 0,
            "eventTypes": {
                "EVENT_RAVEN_UNPLUGGED": {
                    "totalSeconds": 0,
                    "totalEvents": 0
                },
                "EVENT_RAVEN_PLUGGED": {
                    "totalSeconds": 0,
                    "totalEvents": 0
                },
                ...
            }
        } */
    },
    tripSummariesByEntityId: {/* trip data which is used to normalize events for scoring purposes
        "<Entity ID>": {
            "driverName": "DimDriver.name",
            "vehicleName": "DimVehicle.name",
            "totalKMs": 0,
            "totalSeconds": 0
        } */
    },

    // progressive loading of historical data
    // "trend" arrays are in reverse chronological order and provide the same data as the current period ()
    trendScoresSummaries: [/*{ // 
        score: 0,
        periodStartDate: <Date>,
        periodEndDate: <Date>,
        totalKMs: 0,
        totalSeconds: 0,
        totalEventsByType: {
            "EVENT_RAVEN_UNPLUGGED": 0,
            ...
        },
        scoreSummariesByEntityId: {
            "<Entity ID>": {
                "driverName": "DimDriver.name",
                ...
            }
        },
    } */],
    trendTripSummaries: [/*
        {
            "<Entity ID>": {
                "driverName": "DimDriver.name",
                "vehicleName": "DimVehicle.name",
                "totalKMs": 0,
                "totalSeconds": 0
            }
        },
        ...*/
    ],
    // tracking progress of loading historical data
    totalTimePeriodsLoaded: 0, // tracking total entries in safetyScoreTrendData (i.e. avoiding issues with shallow compare by having a basic integer counter)
    progressPercentageComplete: 0
};

export default class SafetyScoreModuleGroup extends React.Component {

    static propTypes = {
        onOptionsClick: PropTypes.func.isRequired,
        onLoadScoreClick: PropTypes.func.isRequired,
        allScoreFactorsAreValid: PropTypes.bool.isRequired,
        refreshReportingTokenPromise: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired,
        unitOfMeasure: PropTypes.string.isRequired,
        selectedTimePeriodType: PropTypes.string,
        selectedReportBy: PropTypes.string,
        availableEntities: PropTypes.array,
        availableEventsFactors: PropTypes.array,
        selectedScoreThresholds: PropTypes.object,
        appliedQueryStateUpdateId: PropTypes.number, // timestamp (Date.now()) used to halt queue processing for an outdated query config state
        stateDivergedFromDefaults: PropTypes.bool.isRequired
    };

    resetStateAndQueues = (stateUpdatedCallback = undefined) => {
        // note: same as constructor
        this.eventsQueriesQueue = [];
        this.eventsQueriesQueueTotalProcessedResponses = 0;
        this.scoreActiveCurrentQueryCount = 0;
        this.eventsQueriesResponseData = {
            data: []
        };
        this.tripsQueriesQueue = [];
        this.tripsQueriesQueueTotalProcessedResponses = 0;
        this.tripsActiveCurrentQueryCount = 0;
        this.tripsQueriesResponseData = {
            data: []
        };

        this.eventsQueriesQueueTotalRequiredResponses = 0;
        this.tripsQueriesQueueTotalRequiredResponses = 0;
        this.setState({
            ...INITIAL_STATE
        }, () => {
            if (stateUpdatedCallback) {
                stateUpdatedCallback();
            }
        });
    };

    constructor(props) {
        
        super(props);

        this.state = {
            ...INITIAL_STATE
        };

        this.eventsQueriesQueue = [];
        this.eventsQueriesQueueTotalProcessedResponses = 0;
        this.scoreActiveCurrentQueryCount = 0;
        this.eventsQueriesResponseData = {
            data: []
        };
        this.tripsQueriesQueue = [];
        this.tripsQueriesQueueTotalProcessedResponses = 0;
        this.tripsActiveCurrentQueryCount = 0;
        this.tripsQueriesResponseData = {
            data: []
        };

        this.eventsQueriesQueueTotalRequiredResponses = 0;
        this.tripsQueriesQueueTotalRequiredResponses = 0;
    }

    componentDidMount() {
        if (this.props.appliedQueryStateUpdateId) {
            this.loadScoreData(this.state.totalTimePeriodsLoaded, this.props.appliedQueryStateUpdateId);
        }
    };
    componentDidUpdate(prevProps) {
        if (this.props.appliedQueryStateUpdateId !== prevProps.appliedQueryStateUpdateId) {
            this.resetStateAndQueues(() => {
                this.loadScoreData(this.state.totalTimePeriodsLoaded, this.props.appliedQueryStateUpdateId);
            });
        }
    };
    componentWillUnmount() {
        this.resetStateAndQueues();
    };

    calculateScore = (totalEventsForType, totalKMSForAllTrips) => {
        if (!totalEventsForType || !totalKMSForAllTrips) {
            return PERFECT_SCORE;
        }
        /*
        At time of writing:
            1. For most event types, we DO NOT have accurate distances or durations.
            2. The trips that contain the events, we DO have accurate distances and durations.
            Therefore, we need a workaround to convert event data to something RELATIVE to the trip data we have.
            We could do this either by using distance or duration, but we need to be consistent.
            For now, we'll use distance by assuming every event has a distance of 1 km and ignore the duration.
        */
        return PERFECT_SCORE - (totalEventsForType * (MILES_TO_KM_FACTOR * EVENT_SCALING_FACTOR) / totalKMSForAllTrips);
    };

    calculateEventScoresByEntity = (eventType, eventSummariesForEntity, tripsSummaryForEntity) => { 

        if (!eventType) {
            console.warn("SafetyScoreModuleGroup calculateEventScoresByEntity eventType is undefined");
            return;
        }

        if (!eventSummariesForEntity) {
            console.warn("SafetyScoreModuleGroup calculateEventScoresByEntity eventSummariesForEntity is undefined");
            return;
        }

        if (!tripsSummaryForEntity) {
            console.warn("SafetyScoreModuleGroup calculateEventScoresByEntity tripsSummaryForEntity is undefined");
            return;
        }

        const totalEventsForType = eventSummariesForEntity.eventTypes[eventType] ? eventSummariesForEntity.eventTypes[eventType].totalEvents : 0;

        if (totalEventsForType === 0) {
            eventSummariesForEntity.eventTypes[eventType].score = PERFECT_SCORE;
            return;
        };

        const totalKMSForAllTrips = tripsSummaryForEntity.totalKMs;

        if (totalKMSForAllTrips === 0) { // RAV-6296: If entity has no trips, do not include them in the score calculation
            eventSummariesForEntity.eventTypes[eventType].score = NOT_APPLICABLE_SCORE;
            return;
        };

        let eventScore = this.calculateScore(totalEventsForType, totalKMSForAllTrips);

        if (eventScore < LOWEST_POSSIBLE_SCORE) {
            eventScore = LOWEST_POSSIBLE_SCORE;
        }

        eventSummariesForEntity.eventTypes[eventType].score = eventScore;
    };

    // updates the provided references and returns fleet summary data
    calculateSafetyScoresByEntity = (scoreSummariesByEntityId, tripSummariesByEntityId) => { // IMPORTANT: Arguments are references to the actual objects, so we can modify them directly
        
        const entityIds = Object.keys(scoreSummariesByEntityId);

        let entitiesWithTrips = 0;

        // totals for all entities
        let fleetTotalWeightedFactors = 0;
        let fleetTotalWeights = 0;
        let fleetTotalKMs = 0;
        let fleetTotalSeconds = 0;
        let fleetTotalEventsByType = {};

        entityIds.forEach(entityId => {
            
            const eventSummariesForEntity = scoreSummariesByEntityId[entityId];
            const tripsSummaryForEntity = tripSummariesByEntityId[entityId];
            const eventTypes = Object.keys(eventSummariesForEntity.eventTypes);

            if (!tripsSummaryForEntity) {
                console.warn("SafetyScoreModuleGroup calculateSafetyScoresByEntity tripsSummaryForEntity is undefined");
                return;
            }

            const entityTotalKMs = tripsSummaryForEntity.totalKMs;
            const entityTotalSeconds = tripsSummaryForEntity.totalSeconds;

            // RAV-6296: If entity has no trips, do not include them in the score calculation
            if (entityTotalKMs < MINIMUM_KMS_FOR_SCORE) {
                eventSummariesForEntity.score = NOT_APPLICABLE_SCORE;
                eventTypes.forEach(eventType => {
                    eventSummariesForEntity.eventTypes[eventType].score = NOT_APPLICABLE_SCORE;
                });
                return;
                // by returning, entity's data not included in "totals for all entities:"
                // * not in fleetTotalWeightedFactors
                // * not in fleetTotalWeights
                // * not in fleetTotalKMs
                // * not in fleetTotalSeconds
                // * not in fleetTotalEventsByType
            }
            entitiesWithTrips += 1;

            fleetTotalKMs += entityTotalKMs;
            fleetTotalSeconds += entityTotalSeconds;

            let totalWeightedFactors = 0;
            let totalWeights = 0;

            eventTypes.forEach(eventType => {

                this.calculateEventScoresByEntity(eventType, eventSummariesForEntity, tripsSummaryForEntity); // IMPORTANT: the arguments for safety score and trip are references to the actual objects, so we can modify them directly
                // now eventSummariesForEntity has a score for the current event type

                if (!fleetTotalEventsByType.hasOwnProperty(eventType)) {
                    fleetTotalEventsByType[eventType] = 0;
                }

                fleetTotalEventsByType[eventType] += eventSummariesForEntity.eventTypes[eventType].totalEvents;

                const selectedEventFactor = this.props.availableEventsFactors.find(eventFactor => eventFactor.type === eventType);

                if (!selectedEventFactor) {
                    console.warn("SafetyScoreModuleGroup calculateSafetyScoresByEntity selectedEventFactor is undefined");
                    return;
                }

                const eventWeight = selectedEventFactor.weight;

                if (eventWeight === undefined) {
                    console.warn("SafetyScoreModuleGroup calculateSafetyScoresByEntity eventWeight is undefined");
                    return;
                }

                const eventScore = eventSummariesForEntity.eventTypes[eventType].score;
                if (eventScore === NOT_APPLICABLE_SCORE) {
                    return;
                }
                totalWeightedFactors += eventScore * eventWeight;
                totalWeights += eventWeight;
            });

            eventSummariesForEntity.score = totalWeightedFactors / totalWeights;
            fleetTotalWeightedFactors += totalWeightedFactors;
            fleetTotalWeights += totalWeights;
        });

        if (entitiesWithTrips === 0) {
            // no trips for any entities, so return a perfect score and zero totals
            return {
                score: NOT_APPLICABLE_SCORE,
                totalKMs: 0,
                totalSeconds: 0,
                totalEventsByType: fleetTotalEventsByType
            };
        }

        return {
            score: fleetTotalWeightedFactors / fleetTotalWeights,
            totalKMs: fleetTotalKMs,
            totalSeconds: fleetTotalSeconds,
            totalEventsByType: fleetTotalEventsByType
        };
    };

    dateRangeForSelectedTimePeriod = (trendDataRequestNumber) => {

        if (!this.props.selectedTimePeriodType) {
            console.warn("SafetyScoreModuleGroup dateRangeForSelectedTimePeriod selectedTimePeriodType is undefined");
            //return; commented-out to allow for default time period
        };

        let endDate = new Date(); // @ current local time
        let currentMonth = endDate.getMonth(); // January - December: 0 - 11
        let currentDayOfTheWeek = endDate.getDay(); // Sunday - Saturday: 0 - 6
        let startDate;

        switch (this.props.selectedTimePeriodType) {

            case SUPPORTED_TIME_PERIOD_TYPES.QUARTERLY:

                // for trendDataRequestNumber = 0, endDate is today and startDate is the first day of the current quarter;
                // for trendDateRequestNumber > 1, endDate is the last day of the previous quarter and startDate is the first day of the previous quarter, etc.
                
                // get the current month and snap back to the first month of the current quarter (quarters are Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec)
                let currentQuarter = 1;
                if (currentMonth < 3) {
                    //currentQuarter = 1;
                    startDate = new Date(endDate.getFullYear(), 0, 1); // current year, Jan 1 @ current local time
                } else if (currentMonth < 6) {
                    currentQuarter = 2;
                    startDate = new Date(endDate.getFullYear(), 3, 1); // current year, Apr 1 @ current local time
                } else if (currentMonth < 9) {
                    currentQuarter = 3;
                    startDate = new Date(endDate.getFullYear(), 6, 1); // current year, Jul 1 @ current local time
                } else {
                    currentQuarter = 4;
                    startDate = new Date(endDate.getFullYear(), 9, 1); // current year, Oct 1 @ current local time
                }


                if (trendDataRequestNumber === 0) break; // current quarter, no need to shift dates

                // snaps dates to the first and last days of the current quarter
                const lastMonthIndexOfCurrentQuarter = (currentQuarter * 3) - 1;
                const firstMonthIndexOfCurrentQuarter = lastMonthIndexOfCurrentQuarter - 2;
                endDate = new Date(endDate.getFullYear(), lastMonthIndexOfCurrentQuarter + 1, 0); // current year, next month after current quarter's last month, day 0 means last day of PREVIOUS month (i.e. last day of current quarter)
                startDate = new Date(startDate.getFullYear(), firstMonthIndexOfCurrentQuarter, 1); // current year, first day of the current quarter

                // shift dates back by the current trendDataRequestNumber of quarters
                for (let i = 0; i < trendDataRequestNumber; i++) {
                    endDate = new Date(endDate.getFullYear(), endDate.getMonth() - 2, 0); // subtract 2 months and use 0 day to get the last day of the previous quarter
                    startDate = new Date(startDate.getFullYear(), startDate.getMonth() - 3, 1); // subtract 3 months and use 1 day to get the first day of the previous quarter
                }

                break;

            case SUPPORTED_TIME_PERIOD_TYPES.MONTHLY:

                // for trendDataRequestNumber = 0, endDate is today and startDate is the first day of the current month;
                // for trendDateRequestNumber > 1, endDate is the last day of the previous month and startDate is the first day of the previous month, etc.
                startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1); // current year, current month, 1st day

                if (trendDataRequestNumber === 0) break; // current month, no need to shift dates

                // shift dates back by the current trendDataRequestNumber of months
                for (let i = 0; i < trendDataRequestNumber; i++) {
                    endDate = new Date(endDate.getFullYear(), endDate.getMonth(), 0); // subtract 1 month and use 0 day to get the last day of the previous month
                    startDate = new Date(startDate.getFullYear(), startDate.getMonth() - 1, 1); // subtract 1 month and use 1 day to get the first day of the previous month
                }

                break;

            case SUPPORTED_TIME_PERIOD_TYPES.WEEKLY:
            default:

                // for trendDataRequestNumber = 0, endDate is today and startDate is the first day of the current week;
                // for trendDateRequestNumber > 1, endDate is the last day of the previous week and startDate is the first day of the previous week, etc.
                startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - currentDayOfTheWeek); // current year, current month, current day - current day of the week

                if (trendDataRequestNumber === 0) break; // current week, no need to shift dates

                // shift dates back by the current trendDataRequestNumber of weeks
                for (let i = 0; i < trendDataRequestNumber; i++) {
                    let millisecondTimestampFor7DaysAgo = endDate.getTime() - (7 * 24 * 60 * 60 * 1000);
                    endDate.setTime(millisecondTimestampFor7DaysAgo);
                    // shift endDate to end of its current week
                    endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + (6 - endDate.getDay())); // current year, current month, current day + (6 - current day of the week)
                    // shirt startDate to start of endDate's current week
                    startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - endDate.getDay()); // current year, current month, current day - 6
                }

                break;
        }

        return [startDate, endDate];
    };

    pagedTimeDimensionsForDateRange = (dateRange, maxDaysPerPage = MAX_DAYS_FOR_EVENTS_QUERY_PAGINATION) => {

        // break up dateRange into an array of date ranges for each day (ie. [["YYYY-MM-DD", "YYYY-MM-DD"], ["YYYY-MM-DD", "YYYY-MM-DD"], ...])
        // assumption/expected: this.dateRangeForSelectedTimePeriod() returns a startDate at 00:00:00 and endDate at 23:59:59
        const startDate = dateRange[0];
        const endDate = dateRange[1];

        // create an array of date strings ("YYYY-MM-DD") for each day in the date range
        const allDates = [];
        const currentDate = new Date(startDate);
        const endDateTime = endDate.getTime();
        while (currentDate.getTime() <= endDateTime) {
            allDates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // create an array of date ranges for each page of maxDaysPerPage days
        const dateRanges = [];
        for (let i = 0; i < allDates.length; i += maxDaysPerPage) {
            const pageStartDate = new Date(allDates[i]);
            // set pageStartDate time to the beginning to the day
            pageStartDate.setHours(0, 0, 0, 0);
            const pageEndDate = new Date(allDates[Math.min(i + maxDaysPerPage - 1, allDates.length - 1)]);
            // set pageEndDate time to the end of the day
            pageEndDate.setHours(23, 59, 59, 999);
            dateRanges.push([pageStartDate.toISOString(), pageEndDate.toISOString()]);
        }
        return dateRanges;
    };

    eventTypeFilter = () => {

        if (!this.props.availableEventsFactors) {
            console.warn("SafetyScoreModuleGroup eventTypeFilter availableEventsFactors is undefined");
            return;
        }

        if (!this.props.availableEventsFactors.length) {
            console.warn("SafetyScoreModuleGroup eventTypeFilter availableEventsFactors is empty");
            return;
        }
        
        // list the "type" property of any selected event factor with a "weight" property greater than 0
        const eventTypeValues = this.props.availableEventsFactors.filter(eventFactor => eventFactor.weight > 0).map(eventFactor => eventFactor.type);

        return {
            "dimension": "DimEventType.name",
            "operator": "equals",
            "values": eventTypeValues // an array of event types derived from this.props.availableEventsFactors
        };
    }

    driverIdentityIdFilter = () => {

        if (!this.props.selectedReportBy) {
            console.warn("SafetyScoreModuleGroup driverIdentityIdFilter selectedReportBy is undefined");
            return;
        };

        if (this.props.selectedReportBy !== REPORT_BY_TYPES.DRIVER) {
            // omit the driver identity id filter if the selected report-by is not driver
            return;
        };

        if (!this.props.availableEntities) {
            console.warn("SafetyScoreModuleGroup driverIdentityIdFilter availableEntities is undefined");
            return;
        };

        if (!this.props.availableEntities.length) {
            console.warn("SafetyScoreModuleGroup driverIdentityIdFilter availableEntities is empty");
            return;
        };

        const selectedEntities = this.props.availableEntities.filter(entity => entity.selected);

        if (!selectedEntities.length) {
            console.warn("SafetyScoreModuleGroup driverIdentityIdFilter selectedEntities is empty");
            return;
        }

        const entityIds = selectedEntities.map(entity => entity.id);

        return {
            "dimension": "DimDriver.identityId",
            "operator": "equals",
            "values": entityIds // array of driver identity ids when selectedReportBy is by driver
        };
    }

    vehicleNameFilter = () => {

        if (!this.props.selectedReportBy) {
            console.warn("SafetyScoreModuleGroup vehicleNameFilter selectedReportBy is undefined");
            return;
        };

        if (this.props.selectedReportBy !== REPORT_BY_TYPES.VEHICLE) {
            // omit the vehicle name filter if the selected report-by is not vehicle
            return;
        };

        if (!this.props.availableEntities) {
            console.warn("SafetyScoreModuleGroup vehicleNameFilter availableEntities is undefined");
            return;
        };

        if (!this.props.availableEntities.length) {
            console.warn("SafetyScoreModuleGroup vehicleNameFilter availableEntities is empty");
            return;
        }

        const selectedEntities = this.props.availableEntities.filter(entity => entity.selected);

        if (!selectedEntities.length) {
            console.warn("SafetyScoreModuleGroup vehicleNameFilter selectedEntities is empty");
            return;
        }

        const entityNames = selectedEntities.map(entity => entity.name);

        return {
            "dimension": "DimVehicle.name",
            "operator": "equals",
            "values": entityNames // array of vehicle names when selectedReportBy is by vehicle
        };
    }

    queryFilters = (requestType = REQUEST_TYPE.EVENTS) => {

        const filters = [];

        if (requestType === REQUEST_TYPE.EVENTS) {
            const eventTypeFilter = this.eventTypeFilter();
            if (eventTypeFilter) filters.push(eventTypeFilter);
            filters.push({
                "dimension": "FactEvent.hidden",
                "operator": "equals",
                "values": ['0']
            });
        }

        const driverIdentityIdFilter = this.driverIdentityIdFilter();
        if (driverIdentityIdFilter) filters.push(driverIdentityIdFilter);

        const vehicleNameFilter = this.vehicleNameFilter();
        if (vehicleNameFilter) filters.push(vehicleNameFilter);

        if (requestType === REQUEST_TYPE.TRIP) {
            // add a filter to only include segments that are  "FactSegment.type": "driving"
            filters.push({
                "dimension": "FactSegment.type",
                "operator": "equals",
                "values": ["driving"]
            });
        }

        return filters;
    };

    reportingApiRequest = (reportingTokenAndApiUrl, query) => {
        // get token and apiUrl from reportingTokenAndApiUrl and then run a basic fetch API request
        const { token, apiUrl } = reportingTokenAndApiUrl;
        const requestObject = {
            headers: {
                'Authorization': token,
                'Content-Type': 'application/json'
            }
        };
        const queryJson = JSON.stringify(query);
        const requestUrl = `${apiUrl}/load?query=${encodeURIComponent(queryJson)}`;
        return fetch(requestUrl, requestObject)
        .then(
            (response) => {
                if (response.status >= 300) {
                    return Promise.reject(new Error("Bad response from server"));
                }
                return response.json();
            }
        )
        .catch(
            (error) => {
                console.error(error);
            }
        );
    };

    updateTotalRequestsProgress = () => {

        const totalRequestsPerTimePeriod = this.eventsQueriesQueueTotalRequiredResponses + this.tripsQueriesQueueTotalRequiredResponses;

        // Assumption: the lengths are the same for each timeperiod (current and historical periods)
        const totalRequestsForAllTimePeriods = totalRequestsPerTimePeriod * (MAX_SCORE_TREND_PREVIOUS_PERIODS + 1); // + 1 for current/present time period

        const totalRequestsForCompletedTimePeriods = this.state.totalTimePeriodsLoaded * totalRequestsPerTimePeriod;

        const totalRequestsCompletedForCurrentTimePeriod = (this.eventsQueriesQueueTotalRequiredResponses - this.eventsQueriesQueue.length) + (this.tripsQueriesQueueTotalRequiredResponses - this.tripsQueriesQueue.length);

        const totalRequestsCompleted = totalRequestsForCompletedTimePeriods + totalRequestsCompletedForCurrentTimePeriod;

        let progressPercentageComplete = Math.round((totalRequestsCompleted / totalRequestsForAllTimePeriods) * 100);

        if (progressPercentageComplete > 100) {
            progressPercentageComplete = 100;
        }

        this.setState({
            progressPercentageComplete: progressPercentageComplete
        });
    }



    processTripQueriesQueue = (queueFinishedCallback, queryStateUpdateId) => {

        if (!queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup processTripQueriesQueue queryStateUpdateId is undefined");
            return;
        }
        if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup processTripQueriesQueue queryStateUpdateId does not match appliedQueryStateUpdateId");
            return;
        }

        this.updateTotalRequestsProgress();

        let error = undefined;
        if (this.tripsQueriesQueueTotalProcessedResponses === this.tripsQueriesQueueTotalRequiredResponses) {
            this.tripsQueriesQueueTotalProcessedResponses = 0; // reset
            queueFinishedCallback(error, this.tripsQueriesResponseData);;
            return;
        }

        while (this.tripsActiveCurrentQueryCount < MAX_CONCURRENT_API_REQUESTS && this.tripsQueriesQueue.length > 0) {

            if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
                console.warn("SafetyScoreModuleGroup processTripQueriesQueue appliedQueryStateUpdateId does not match queryStateUpdateId");
                return;
            }

            const query = this.tripsQueriesQueue.shift();

            this.tripsActiveCurrentQueryCount += 1;

            this.props.refreshReportingTokenPromise()
            .then(
                (reportingTokenAndApiUrl) => {
                    return this.reportingApiRequest(reportingTokenAndApiUrl, query);
                }
            )
            .then(
                (response) => {

                    this.tripsActiveCurrentQueryCount -= 1;

                    if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
                        console.warn("SafetyScoreModuleGroup processTripQueriesQueue appliedQueryStateUpdateId does not match queryStateUpdateId");
                        return;
                    }

                    let slowQuery = response.slowQuery;

                    if (response.error) {
                        if (response.error === "Continue wait") { // https://cube.dev/docs/product/apis-integrations/rest-api#continue-wait
                            slowQuery = true;
                            this.tripsQueriesQueue.unshift(query); // retry the query
                        } else {
                            console.error(response);
                            queueFinishedCallback(error);
                            return;
                        }
                    } else {
                        this.tripsQueriesQueueTotalProcessedResponses += 1;
                        this.tripsQueriesResponseData.data = [...this.tripsQueriesResponseData.data, ...response.data];
                    }

                    let continueTimeout = 0;
                    if (slowQuery) {
                        continueTimeout = SLOW_QUERY_RETRY_TIMEOUT;
                    }
                    setTimeout(() => {
                        this.processTripQueriesQueue(queueFinishedCallback, queryStateUpdateId);
                    }, continueTimeout);
                },
                (error) => {

                    if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
                        console.warn("SafetyScoreModuleGroup processTripQueriesQueue appliedQueryStateUpdateId does not match queryStateUpdateId");
                        return;
                    }

                    console.error(error);
                    queueFinishedCallback(error, this.tripsQueriesResponseData);
                }
            );
        }
    };

    requestTripData = (trendDataRequestNumber = 0, queryStateUpdateId) => { // if trendDataRequestNumber = 0, this request is for 

        if (!queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup requestTripData queryStateUpdateId is undefined");
            return;
        }
        if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup requestTripData queryStateUpdateId does not match appliedQueryStateUpdateId");
            return;
        }

        const timeDimensionsDateRange = this.dateRangeForSelectedTimePeriod(trendDataRequestNumber);

        // break up timeDimensionsDateRange into an array of date ranges for each day (ie. [["YYYY-MM-DD", "YYYY-MM-DD"], ["YYYY-MM-DD", "YYYY-MM-DD"], ...])
        // assumption/expected: this.dateRangeForSelectedTimePeriod() returns a startDate at 00:00:00 and endDate at 23:59:59
        const dateRanges = this.pagedTimeDimensionsForDateRange(timeDimensionsDateRange, MAX_DAYS_FOR_TRIP_QUERY_PAGINATION);

        this.tripsQueriesQueue = dateRanges.map(dateRange => {
            const query = {
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                measures: [
                    "FactSegment.distance",
                    "FactSegment.durationInSeconds"
                ],
                timeDimensions: [{
                    dimension: "FactSegment.startTime",
                    dateRange: dateRange
                }],
                filters: this.queryFilters(REQUEST_TYPE.TRIP),
                dimensions: [
                    "FactSegment.segId",
                    //"DimDriver.name", // conditionally added below
                    //"DimDriver.identityId", // conditionally added below
                    "DimVehicle.name",
                    "DimRaven.uuid",
                    "FactSegment.type",
                    "FactSegment.startTime",
                    "FactSegment.endTime"
                ]
            };
            if (this.props.selectedReportBy === REPORT_BY_TYPES.DRIVER) {
                query.dimensions.push("DimDriver.name");
                query.dimensions.push("DimDriver.identityId");
            }
            return query;
        });

        this.tripsQueriesQueueTotalRequiredResponses = this.tripsQueriesQueue.length;

        // wrap processTripQueriesQueue in a promise that returns when the queue is empty
        return new Promise((resolve, reject) => {
            this.tripsQueriesResponseData = {
                data: []
            };
            this.processTripQueriesQueue((error, amalgamatedResponse) => {
                if (error) {
                    reject(error);
                } // else
                resolve(amalgamatedResponse)
            }, queryStateUpdateId);
        });
    };

    processEventQueriesQueue = (queueFinishedCallback, queryStateUpdateId) => {

        if (!queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup processEventQueriesQueue queryStateUpdateId is undefined");
            return;
        }
        if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup processEventQueriesQueue queryStateUpdateId does not match appliedQueryStateUpdateId");
            return;
        }

        this.updateTotalRequestsProgress();

        let error = undefined;
        if (this.eventsQueriesQueueTotalProcessedResponses === this.eventsQueriesQueueTotalRequiredResponses) {
            this.eventsQueriesQueueTotalProcessedResponses = 0; // reset
            queueFinishedCallback(error, this.eventsQueriesResponseData);
            return;
        }

        while (this.scoreActiveCurrentQueryCount < MAX_CONCURRENT_API_REQUESTS && this.eventsQueriesQueue.length > 0) {

            if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
                console.warn("SafetyScoreModuleGroup processEventQueriesQueue appliedQueryStateUpdateId does not match queryStateUpdateId");
                return;
            }

            const query = this.eventsQueriesQueue.shift();
            
            this.scoreActiveCurrentQueryCount += 1;

            this.props.refreshReportingTokenPromise()
            .then(
                (reportingTokenAndApiUrl) => {
                    return this.reportingApiRequest(reportingTokenAndApiUrl, query);
                }
            )
            .then(
                (response) => {

                    this.scoreActiveCurrentQueryCount -= 1;

                    if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
                        console.warn("SafetyScoreModuleGroup processEventQueriesQueue appliedQueryStateUpdateId does not match queryStateUpdateId");
                        return;
                    }

                    let slowQuery = response.slowQuery;

                    if (response.error) {
                        if (response.error === "Continue wait") { // https://cube.dev/docs/product/apis-integrations/rest-api#continue-wait
                            slowQuery = true;
                            this.eventsQueriesQueue.unshift(query); // retry the query
                        } else {
                            console.error(response);
                            queueFinishedCallback(error);
                            return;
                        }
                    } else {
                        this.eventsQueriesQueueTotalProcessedResponses += 1;
                        this.eventsQueriesResponseData.data = [...this.eventsQueriesResponseData.data, ...response.data];
                    }
                    
                    let continueTimeout = 0;
                    if (slowQuery) {
                        continueTimeout = SLOW_QUERY_RETRY_TIMEOUT;
                    }
                    setTimeout(() => {
                        this.processEventQueriesQueue(queueFinishedCallback, queryStateUpdateId);
                    }, continueTimeout);
                },
                (error) => {

                    if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
                        console.warn("SafetyScoreModuleGroup processEventQueriesQueue appliedQueryStateUpdateId does not match queryStateUpdateId");
                        return;
                    }

                    console.error(error);
                    queueFinishedCallback(error);
                }
            );
        }
    };

    requestEventData = (trendDataRequestNumber = 0, queryStateUpdateId) => { // if trendDataRequestNumber = 0, this request is for scoreSummariesByEntityId; otherwise, this is a trendScoresSummaries-related request

        if (!queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup requestEventData queryStateUpdateId is undefined");
            return;
        }
        if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup requestEventData queryStateUpdateId does not match appliedQueryStateUpdateId");
            return;
        }

        const timeDimensionsDateRange = this.dateRangeForSelectedTimePeriod(trendDataRequestNumber);

        // break up timeDimensionsDateRange into an array of date ranges for each day (ie. [["YYYY-MM-DD", "YYYY-MM-DD"], ["YYYY-MM-DD", "YYYY-MM-DD"], ...])
        // assumption/expected: this.dateRangeForSelectedTimePeriod() returns a startDate at 00:00:00 and endDate at 23:59:59
        const dateRanges = this.pagedTimeDimensionsForDateRange(timeDimensionsDateRange, MAX_DAYS_FOR_EVENTS_QUERY_PAGINATION);

        this.eventsQueriesQueue = dateRanges.map(dateRange => {
            const query = {
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                timeDimensions: [{
                    dimension: "FactEvent.timestamp",
                    dateRange: dateRange
                }],
                filters: this.queryFilters(REQUEST_TYPE.EVENTS),
                dimensions: [
                    //"DimDriver.name", // conditionally added below
                    //"DimDriver.identityId", // conditionally added below
                    "DimVehicle.name",
                    "FactEvent.timestamp",
                    "FactEvent.position",
                    "DimEventType.name",
                    "DimRaven.uuid",
                    "DimCard.cardId"
                ]
            };
            if (this.props.selectedReportBy === REPORT_BY_TYPES.DRIVER) {
                query.dimensions.push("DimDriver.name");
                query.dimensions.push("DimDriver.identityId");
            }
            return query;
        });

        this.eventsQueriesQueueTotalRequiredResponses = this.eventsQueriesQueue.length;

        // wrap processEventQueriesQueue in a promise that returns when the queue is empty
        return new Promise((resolve, reject) => {
            this.eventsQueriesResponseData = {
                data: []
            };
            this.processEventQueriesQueue((error) => {
                if (error) {
                    reject(error);
                } // else
                resolve(this.eventsQueriesResponseData)
            }, queryStateUpdateId);
        });
    };

    convertDataToEventTypesSummariesByEntityId = (data) => {

        /* data is an array of objects with the following properties:
            "FactSegment2.segId":
            "DimVehicle.name"
            "DimDriver.name"
            "DimDriver.identityId"
            "FactEvent.timestamp"
            "FactEvent.position"
            "DimEventType.name"
            "DimRaven.uuid"
            "DimCard.cardId"

        return an object with the following properties:
            "<Entity ID>": {
                "driverName": "DimDriver.name",
                "vehicleName": "DimVehicle.name",
                "eventTypes": {
                    "EVENT_RAVEN_UNPLUGGED": {
                        "totalEvents": 0
                    },
                    "EVENT_RAVEN_PLUGGED": {
                        "totalEvents": 0
                    },
                    ...
                }
            }
        */
        const eventTypesSummariesByEntityId = {};
        const requestedEntityIds = this.props.availableEntities.filter(entity => entity.selected).map(entity => entity.id);

        // initiate the eventTypesSummariesByEntityId object with the requested entity ids
        requestedEntityIds.forEach(entityId => {
            const entityName = this.props.availableEntities.find(entity => entity.id === entityId).name
            eventTypesSummariesByEntityId[entityId] = {
                vehicleName: entityName,
                driverName: entityName,
                eventTypes: {}
            };
            Object.keys(SUPPORTED_EVENTS_FACTORS_TYPES).forEach(eventFactorName => {

                const eventType = SUPPORTED_EVENTS_FACTORS_TYPES[eventFactorName];

                eventTypesSummariesByEntityId[entityId].eventTypes[eventType] = {
                    totalEvents: 0
                }
            });
        });

        data.forEach(event => {

            let entityId;

            switch (this.props.selectedReportBy) {
                case REPORT_BY_TYPES.DRIVER:
                    entityId = event["DimDriver.identityId"];
                    break;
                case REPORT_BY_TYPES.VEHICLE:
                    entityId = event["DimRaven.uuid"];
                    break;
                default:
                    console.warn("SafetyScoreModuleGroup convertDataToEventTypesSummariesByEntityId selectedReportBy is not supported. Defaulting to vehicle.");
                    entityId = event["DimRaven.uuid"];
                    break;
            };

            eventTypesSummariesByEntityId[entityId].vehicleName = event["DimVehicle.name"];
            if (event["DimDriver.name"]) {
                eventTypesSummariesByEntityId[entityId].driverName = event["DimDriver.name"];
            }

            const eventType = event["DimEventType.name"];

            if (!eventTypesSummariesByEntityId[entityId].eventTypes[eventType]) {
                console.warn("SafetyScoreModuleGroup convertDataToEventTypesSummariesByEntityId unexpected eventType", eventType);
                return;
            }

            eventTypesSummariesByEntityId[entityId].eventTypes[eventType].totalEvents += 1;
        });

        return eventTypesSummariesByEntityId;
    };

    convertDataToTripSummariesByEntityId = (data) => {
        /* data is an array of objects with the following properties:
            "DimVehicle.name":"1RVP362000181",
            "DimRaven.uuid"
            "FactSegment.segId"
            "FactSegment.type"
            "FactSegment.startTime"
            "FactSegment.endTime"
            "DimDriver.name"
            "DimDriver.identityId"
            "FactSegment.distance"
            "FactSegment.durationInSeconds"
        
        return an object with the following properties:
            "<Entity ID>": {
                "driverName": "DimDriver.name",
                "vehicleName": "DimVehicle.name",
                "totalKMs": 0,
                "totalSeconds": 0
            }
        */
        const totalKMsAndTotalSecondsByEntityId = {};
        const requestedEntityIds = this.props.availableEntities.filter(entity => entity.selected).map(entity => entity.id);
        // initiate the totalKMsAndTotalSecondsByEntityId object with the requested entity ids
        requestedEntityIds.forEach(entityId => {
            const entityName = this.props.availableEntities.find(entity => entity.id === entityId).name
            totalKMsAndTotalSecondsByEntityId[entityId] = {
                vehicleName: entityName,
                driverName: entityName,
                totalKMs: 0,
                totalSeconds: 0
            };
        });

        data.forEach(trip => {

            let entityId;
            switch (this.props.selectedReportBy) {
                case REPORT_BY_TYPES.DRIVER:
                    entityId = trip["DimDriver.identityId"];
                    break;
                case REPORT_BY_TYPES.VEHICLE:
                    entityId = trip["DimRaven.uuid"];
                    break;
                default:
                    console.warn("SafetyScoreModuleGroup convertDataToTripSummariesByEntityId selectedReportBy is not supported. Defaulting to vehicle.");
                    entityId = trip["DimRaven.uuid"];
                    break;
            };

            totalKMsAndTotalSecondsByEntityId[entityId].vehicleName = trip["DimVehicle.name"];
            if (trip["DimDriver.name"]) {
                totalKMsAndTotalSecondsByEntityId[entityId].driverName = trip["DimDriver.name"];
            }

            const tripSegmentDistance = trip["FactSegment.distance"];
            const tripSegmentDuration = trip["FactSegment.durationInSeconds"];

            totalKMsAndTotalSecondsByEntityId[entityId].totalKMs += tripSegmentDistance;
            totalKMsAndTotalSecondsByEntityId[entityId].totalSeconds += tripSegmentDuration;
        });

        return totalKMsAndTotalSecondsByEntityId;
    };

    loadScoreData = (totalTimePeriodsLoaded, queryStateUpdateId) => {

        if (!queryStateUpdateId) {
            console.warn("SafetyScoreModuleGroup loadScoreData queryStateUpdateId is undefined");
            return;
        }
        if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
            return; // query is outdated, halt further processing
        }

        Promise.all([
            this.requestEventData(totalTimePeriodsLoaded, queryStateUpdateId),
            this.requestTripData(totalTimePeriodsLoaded, queryStateUpdateId)
        ])
        .then(
            (responses) => {

                if (this.props.appliedQueryStateUpdateId !== queryStateUpdateId) {
                    return; // query is outdated, halt further processing
                }
        
                const timeDimensionsDateRange = this.dateRangeForSelectedTimePeriod(totalTimePeriodsLoaded);
                const startDate = timeDimensionsDateRange[0];
                const endDate = timeDimensionsDateRange[1];

                const safetyScoreResponse = responses[0];
                const tripDataResponse = responses[1];

                const safetyScoreData = safetyScoreResponse.data;
                const tripData = tripDataResponse.data;

                if (!safetyScoreData) {
                    console.warn("SafetyScoreModuleGroup loadScoreData safetyScoreData.data is undefined");
                    return;
                }

                if (!tripData) {
                    console.warn("SafetyScoreModuleGroup loadScoreData tripData.data is undefined");
                    return;
                }

                const scoreSummariesByEntityId = this.convertDataToEventTypesSummariesByEntityId(safetyScoreData);
                const tripSummariesByEntityId = this.convertDataToTripSummariesByEntityId(tripData);

                // ADD score calculations to score and trip summaries REFERENCES
                const fleetSummary = this.calculateSafetyScoresByEntity(scoreSummariesByEntityId, tripSummariesByEntityId);
                
                if (totalTimePeriodsLoaded === 0) {
                    this.setState({
                        score: fleetSummary.score,
                        periodStartDate: startDate,
                        periodEndDate: endDate,
                        totalKMs: fleetSummary.totalKMs,
                        totalSeconds: fleetSummary.totalSeconds,
                        totalEventsByType: fleetSummary.totalEventsByType,
                        totalTimePeriodsLoaded: totalTimePeriodsLoaded + 1,
                        scoreSummariesByEntityId: scoreSummariesByEntityId,
                        tripSummariesByEntityId: tripSummariesByEntityId
                    }, () => { // using callback to ensure async setState is done before continuing
                        if (totalTimePeriodsLoaded < MAX_SCORE_TREND_PREVIOUS_PERIODS) {
                            this.loadScoreData(totalTimePeriodsLoaded + 1, queryStateUpdateId);
                        }
                    });
                } else {
                    this.setState({
                        totalTimePeriodsLoaded: totalTimePeriodsLoaded + 1,
                        trendScoresSummaries: [...this.state.trendScoresSummaries, {
                            score: fleetSummary.score,
                            periodStartDate: startDate,
                            periodEndDate: endDate,
                            totalKMs: fleetSummary.totalKMs,
                            totalSeconds: fleetSummary.totalSeconds,
                            totalEventsByType: fleetSummary.totalEventsByType,
                            scoreSummariesByEntityId: scoreSummariesByEntityId
                        }],
                        trendTripSummaries: [...this.state.trendTripSummaries, tripSummariesByEntityId]
                    }, () => { // using callback to ensure async setState is done before continuing
                        if (totalTimePeriodsLoaded < MAX_SCORE_TREND_PREVIOUS_PERIODS) {
                            this.loadScoreData(totalTimePeriodsLoaded + 1, queryStateUpdateId);
                        }
                    });
                }
                if (totalTimePeriodsLoaded >= MAX_SCORE_TREND_PREVIOUS_PERIODS) {
                    // score data queue finished
                    //console.log(JSON.stringify(this.state));
                }
            }
        )
    };

    onChangeSelectedTimePeriod = (selectedTimePeriodIndex) => {
        if (selectedTimePeriodIndex > MAX_SCORE_TREND_PREVIOUS_PERIODS) {
            console.warn("SafetyScoreModuleGroup onChangeSelectedTimePeriod selectedTimePeriodIndex is out of range");
            return;
        }
        this.setState({
            selectedTimePeriod: selectedTimePeriodIndex
        });
    };

    render() {

        let {
            score,
            periodStartDate,
            periodEndDate,
            totalKMs,
            totalSeconds,
            scoreSummariesByEntityId,
            tripSummariesByEntityId,
            previousPeriodScore
        } = INITIAL_STATE;

        let totalTimePeriodsLoaded = this.state.totalTimePeriodsLoaded;

        // set the above variables based on this.state.selectedTimePeriod, where 0 is the current time period and >1 is the historical time periods
        if (this.state.selectedTimePeriod === 0) {
            score = this.state.score;
            periodStartDate = this.state.periodStartDate;
            periodEndDate = this.state.periodEndDate;
            totalKMs = this.state.totalKMs;
            totalSeconds = this.state.totalSeconds;
            scoreSummariesByEntityId = this.state.scoreSummariesByEntityId;
            tripSummariesByEntityId = this.state.tripSummariesByEntityId;
            previousPeriodScore = this.state.trendScoresSummaries.length > 0 ? this.state.trendScoresSummaries[0].score : undefined;
        } else {

            if (totalTimePeriodsLoaded < this.state.selectedTimePeriod) {
                totalTimePeriodsLoaded = 0; // a hack to force ScoreSummary and ScoreDetailsTable to show a loading spinner for the selected time period
            }

            const historicalTimePeriodIndex = this.state.selectedTimePeriod - 1;

            if (historicalTimePeriodIndex >= this.state.trendScoresSummaries.length) {
                console.warn("SafetyScoreModuleGroup render selectedTimePeriod is out of range");
            } else if (historicalTimePeriodIndex >= this.state.trendTripSummaries.length) {
                console.warn("SafetyScoreModuleGroup render selectedTimePeriod is out of range");
            } else {
                score = this.state.trendScoresSummaries[historicalTimePeriodIndex].score;
                periodStartDate = this.state.trendScoresSummaries[historicalTimePeriodIndex].periodStartDate;
                periodEndDate = this.state.trendScoresSummaries[historicalTimePeriodIndex].periodEndDate;
                totalKMs = this.state.trendScoresSummaries[historicalTimePeriodIndex].totalKMs;
                totalSeconds = this.state.trendScoresSummaries[historicalTimePeriodIndex].totalSeconds;
                scoreSummariesByEntityId = this.state.trendScoresSummaries[historicalTimePeriodIndex].scoreSummariesByEntityId;
                tripSummariesByEntityId = this.state.trendTripSummaries[historicalTimePeriodIndex];
                previousPeriodScore = this.state.trendScoresSummaries.length > historicalTimePeriodIndex + 1 ? this.state.trendScoresSummaries[historicalTimePeriodIndex + 1].score : undefined;
            }

        }
        

        if (isNaN(this.props.appliedQueryStateUpdateId) || this.props.appliedQueryStateUpdateId === null) {
            return (
                <SafetyScoreLandingPage
                    allScoreFactorsAreValid={this.props.allScoreFactorsAreValid}
                    onOptionsClick={this.props.onOptionsClick}
                    onLoadScoreClick={this.props.onLoadScoreClick}
                />
            );
        }

        return (
            <div className="container fleet-reports-page safety-score-page sticky-footer-layout">
                <SafetyScoreHeader
                    stateDivergedFromDefaults={this.props.stateDivergedFromDefaults}
                    selectedTimePeriodType={this.props.selectedTimePeriodType}
                    selectedReportBy={this.props.selectedReportBy}
                    availableEntities={this.props.availableEntities}
                    availableEventsFactors={this.props.availableEventsFactors}
                    progressPercentageComplete={this.state.progressPercentageComplete}
                    onOptionsClick={this.props.onOptionsClick}

                />
                <div className="score-modules">
                    <ScoreSummary
                        totalTimePeriodsLoaded={totalTimePeriodsLoaded}
                        selectedTimePeriod={this.state.selectedTimePeriod}
                        onChangeSelectedTimePeriod={this.onChangeSelectedTimePeriod}
                        selectedTimePeriodType={this.props.selectedTimePeriodType}
                        periodStartDate={periodStartDate}
                        periodEndDate={periodEndDate}
                        selectedReportBy={this.props.selectedReportBy}
                        selectedScoreThresholds={this.props.selectedScoreThresholds}
                        score={score}
                        scoreSummariesByEntityId={scoreSummariesByEntityId}
                        previousPeriodScore={previousPeriodScore}
                    />
                    <ScoreTrend
                        totalTimePeriodsLoaded={this.state.totalTimePeriodsLoaded}
                        selectedTimePeriod={this.state.selectedTimePeriod}
                        selectedTimePeriodType={this.props.selectedTimePeriodType}
                        selectedScoreThresholds={this.props.selectedScoreThresholds}
                        score={this.state.score}
                        progressPercentageComplete={this.state.progressPercentageComplete}
                        periodStartDate={this.state.periodStartDate}
                        periodEndDate={this.state.periodEndDate}
                        trendScoresSummaries={this.state.trendScoresSummaries}
                    />
                    <EventsTrend
                        selectedTimePeriodType={this.props.selectedTimePeriodType}
                        availableEventsFactors={this.props.availableEventsFactors}
                        progressPercentageComplete={this.state.progressPercentageComplete}
                        periodStartDate={this.state.periodStartDate}
                        periodEndDate={this.state.periodEndDate}
                        totalEventsByType={this.state.totalEventsByType}
                        trendScoresSummaries={this.state.trendScoresSummaries}
                    />
                </div>
                <div className="score-modules container-with-sticky-footer">
                    <ScoreDetailsTable
                        totalTimePeriodsLoaded={totalTimePeriodsLoaded}
                        selectedTimePeriod={this.state.selectedTimePeriod}
                        onChangeSelectedTimePeriod={this.onChangeSelectedTimePeriod}
                        unitOfMeasure={this.props.unitOfMeasure}
                        selectedTimePeriodType={this.props.selectedTimePeriodType}
                        selectedReportBy={this.props.selectedReportBy}
                        availableEntities={this.props.availableEntities}
                        availableEventsFactors={this.props.availableEventsFactors}
                        selectedScoreThresholds={this.props.selectedScoreThresholds}
                        periodStartDate={periodStartDate}
                        periodEndDate={periodEndDate}
                        totalKMs={totalKMs}
                        totalSeconds={totalSeconds}
                        score={score}
                        scoreSummariesByEntityId={scoreSummariesByEntityId}
                        tripSummariesByEntityId={tripSummariesByEntityId}
                    />
                </div>
            </div>
        );
    };
}